import { Component } from '@theme/component';
import { debounce, mediaQueryLarge } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();
  #restoringFocus = false;
  #explicitItem = null;
  /** @type {number | null} */
  #submenuHeightFrame = null;
  #submenuObserver = new ResizeObserver(() => this.#scheduleSubmenuHeight());

  connectedCallback() {
    super.connectedCallback();
    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    this.addEventListener('keydown', this.#onKeyDown, { signal });
    this.addEventListener('focusout', this.#onFocusOut, { signal });
    document.addEventListener('pointerdown', this.#onOutsidePointer, { signal });
    mediaQueryLarge.addEventListener('change', this.#onBreakpointChange, { signal });

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    this.querySelectorAll('[ref="submenu[]"]').forEach((panel) => { panel.inert = true; });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#stopSubmenuObserver();
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    if (this.#restoringFocus || event.pointerType === 'touch') return;
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    this.dispatchEvent(new MegaMenuHoverEvent());

    const isDefaultSlot = item.parentElement?.slot !== 'overflow';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    if (isDefaultSlot && !findSubmenu(item)) {
      this.#closeMenu();
      return;
    }

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      this.#explicitItem = null;
      previouslyActiveItem.ariaExpanded = 'false';
      this.#setDisclosureState(previouslyActiveItem, false);
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    this.#setDisclosureState(item, true);
    let submenu = findSubmenu(item);
    this.#stopSubmenuObserver();
    if (submenu) {
      submenu.inert = false;
      this.#submenuObserver.observe(submenu);
      submenu.querySelectorAll('img[loading="lazy"]').forEach((image) => { image.loading = 'eager'; });
    }
    let overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    const submenuHeight = submenu ? Math.max(submenu.offsetHeight, overflowMenuHeight) : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (event.relatedTarget instanceof Node && item?.parentElement?.contains(event.relatedTarget)) return;
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem, force = false) => {
    if (!item || item != this.#state.activeItem) return;
    if (!force && this.overflowHovered) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    this.#setDisclosureState(item, false);
    this.#explicitItem = null;
    this.#stopSubmenuObserver();
    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  #setDisclosureState(item, expanded) {
    const panel = findSubmenu(item);
    if (!panel) return;
    panel.inert = !expanded;
  }

  #scheduleSubmenuHeight() {
    if (this.#submenuHeightFrame !== null) return;
    this.#submenuHeightFrame = requestAnimationFrame(() => {
      this.#submenuHeightFrame = null;
      this.#updateSubmenuHeight();
    });
  }

  #stopSubmenuObserver() {
    this.#submenuObserver.disconnect();
    if (this.#submenuHeightFrame !== null) {
      cancelAnimationFrame(this.#submenuHeightFrame);
      this.#submenuHeightFrame = null;
    }
  }

  #updateSubmenuHeight() {
    const submenu = findSubmenu(this.#state.activeItem);
    if (!submenu) return;
    const height = `${Math.max(submenu.offsetHeight, this.overflowMenu?.offsetHeight ?? 0)}px`;
    if (this.style.getPropertyValue('--submenu-height') !== height) {
      this.style.setProperty('--submenu-height', height);
    }
  }

  #closeMenu() {
    this.#debouncedActivateHandler.cancel();
    this.#debouncedDeactivate.cancel();
    this.#deactivate(this.#state.activeItem, true);
  }

  #onOutsidePointer = (event) => {
    if (event.target instanceof Node && !this.contains(event.target)) this.#closeMenu();
  };

  #onFocusOut = (event) => {
    if (!(event.relatedTarget instanceof Node) || !this.contains(event.relatedTarget)) this.#closeMenu();
  };

  #onBreakpointChange = () => {
    if (!mediaQueryLarge.matches) this.#closeMenu();
    else this.#updateSubmenuHeight();
  };

  #onKeyDown = (event) => {
    if (event.key === 'Escape' && this.#state.activeItem) {
      event.preventDefault();
      event.stopPropagation();
      const item = this.#state.activeItem;
      const focusTarget = item.parentElement?.slot === 'overflow' ? this.querySelector('[slot="more"] button') : item;
      this.#closeMenu();
      this.#restoringFocus = true;
      focusTarget?.focus({ preventScroll: true });
      this.#restoringFocus = false;
    } else if (event.key === 'ArrowDown' && event.target instanceof Element && event.target.closest('[ref="menuitem"], [slot="more"] button')) {
      const item = findMenuItem(event.target);
      const overflowItem = item?.parentElement?.slot === 'overflow';
      const panel = findSubmenu(item) ?? (overflowItem ? this.overflowMenu : null);
      if (!panel) return;
      event.preventDefault();
      this.#activateHandler(event);
      const firstLink = overflowItem ? this.querySelector('[slot="overflow"] [ref="menuitem"]') : panel.querySelector('a[href], button:not([disabled])');
      firstLink?.focus({ preventScroll: true });
    }
  };

  toggle(event) {
    event.preventDefault();
    event.stopPropagation();
    const item = findMenuItem(event.target);
    if (item === this.#state.activeItem && item === this.#explicitItem) this.#closeMenu();
    else {
      this.#activateHandler(event);
      this.#explicitItem = item;
    }
  }

}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  const listItem = element.closest('.menu-list__list-item');
  if (listItem?.matches('[slot="more"]')) {
    return findMenuItem(listItem.parentElement?.querySelector('[slot="overflow"]'));
  }
  return listItem?.querySelector('[ref="menuitem"]') ?? null;
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
